import React, { FC, ReactElement, useEffect, useLayoutEffect, useState } from 'react'
import Head from 'next/head'
import { AppProps } from 'next/app'
import Script from 'next/script'
import { authHelper, useAuthDataStore, useBindingsStore } from '@entities/user'
import {
  useInitUtmColizeum,
  useInitUtmIsMkSmsGames,
  useInitUtmMembrana,
  useInitUtmMtsDengi,
  useInitUtmPayment,
  useInitUtmSource,
  useInitUtmSourceLauncher,
  useInitUtmTools,
} from '@shared/lib/utm'
import { NextPageWithLayout, usePreviousRoute } from '@shared/lib/next'
import { gibInit } from '@shared/lib/gib'
import { IB_SERVICE_URL } from '@shared/config/paths'
import { SearchPayee } from '@entities/tsp'
import { SERVICE_DESCRIPTION, SERVICE_TITLE } from './config/const/serviceInfo'
import { mtsAnaliticScript } from './config/scripts/mtsAnaliticScript'
import { gtmScript } from './config/scripts/gtmScript'
import { gtmNoScript } from './config/scripts/gtmNoScript'
import OldThemeProvider from './providers/OldThemeProvider'
import BreadCrumbsProvider from './providers/BreadCrumbsProvider'
import PartnerProvider from './providers/PartnerProvider'
import { CyberLayout } from './layouts/CyberLayout'
import ThemeProvider from './providers/ThemeProvider'
import ToastProvider from './providers/ToastProvider'
import { cdekAnalyticsScript } from './config/scripts/cdekAnalyticsScript'
import { cdekAnalyticsNoscript } from './config/scripts/cdekAnalyticsNoscript'

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

interface PageProps {
  servicesForSearch?: SearchPayee[]
}

type AppPropsWithLayout<P> = AppProps<P> & {
  Component: NextPageWithLayout<P>
}

const App: FC<AppPropsWithLayout<PageProps>> = ({ Component, pageProps }) => {
  const [isGibInit, setIsGibInit] = useState(false)

  const {
    setIsAuth,
    isAuth,
    setUserData,
    fetchProfile,
    operatorMTS,
    paymentBanMTS,
    errorOperatorInfo,
    mtsData,
    setMtsData,
  } = useAuthDataStore()
  const { isNeedUpdateBindings, fetchBindings } = useBindingsStore()

  usePreviousRoute()
  useInitUtmSource()
  useInitUtmSourceLauncher()
  useInitUtmMembrana()
  useInitUtmMtsDengi()
  useInitUtmTools()
  useInitUtmColizeum()
  useInitUtmPayment()
  useInitUtmIsMkSmsGames()

  useEffect(() => {
    gibInit(setIsGibInit)
  }, [])

  useEffect(() => {
    if (isAuth && !mtsData && isGibInit) {
      authHelper.initMtsToken(setMtsData)
    }
  }, [isAuth, mtsData, setMtsData, isGibInit])

  useEffect(() => {
    if (isAuth && mtsData?.phone) {
      fetchProfile()
    }
  }, [isAuth, mtsData, setMtsData, fetchProfile])

  useEffect(() => {
    if (isAuth) {
      if (isNeedUpdateBindings && operatorMTS && !paymentBanMTS && !errorOperatorInfo) {
        fetchBindings()
      }

      if (isGibInit && window.gib) {
        window.gib.setAuthStatus(window.gib.IS_AUTHORIZED || false)
      }
    }
  }, [
    fetchProfile,
    fetchBindings,
    isAuth,
    isNeedUpdateBindings,
    operatorMTS,
    isGibInit,
    paymentBanMTS,
    errorOperatorInfo,
  ])

  useIsomorphicLayoutEffect(() => {
    authHelper.initialize()

    if (authHelper.isAuthenticated()) {
      setIsAuth(true)
      setUserData(authHelper.userData)
    }
  }, [])

  const getLayout = Component.getLayout ?? ((page: ReactElement) => <CyberLayout>{page}</CyberLayout>)

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{SERVICE_TITLE}</title>
        <meta name="description" content={SERVICE_DESCRIPTION} />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="apple-touch-icon" sizes="57x57" href="/cyber/favicons/apple-icon-57x57.png" />
        <link rel="apple-touch-icon" sizes="60x60" href="/cyber/favicons/apple-icon-60x60.png" />
        <link rel="apple-touch-icon" sizes="72x72" href="/cyber/favicons/apple-icon-72x72.png" />
        <link rel="apple-touch-icon" sizes="76x76" href="/cyber/favicons/apple-icon-76x76.png" />
        <link rel="apple-touch-icon" sizes="114x114" href="/cyber/favicons/apple-icon-114x114.png" />
        <link rel="apple-touch-icon" sizes="120x120" href="/cyber/favicons/apple-icon-120x120.png" />
        <link rel="apple-touch-icon" sizes="144x144" href="/cyber/favicons/apple-icon-144x144.png" />
        <link rel="apple-touch-icon" sizes="152x152" href="/cyber/favicons/apple-icon-152x152.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/cyber/favicons/apple-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="192x192" href="/cyber/favicons/android-icon-192x192.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/cyber/favicons/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="96x96" href="/cyber/favicons/favicon-96x96.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/cyber/favicons/favicon-16x16.png" />
        <link rel="manifest" href="/cyber/favicons/manifest.json" />
        <script defer src={IB_SERVICE_URL} />
        {typeof document !== 'undefined' && document.documentElement.dataset.partner === 'cdek' && (
          <>
            <script
              defer
              id="cdek-yandex-metrika"
              dangerouslySetInnerHTML={{
                __html: cdekAnalyticsScript,
              }}
            />

            <noscript dangerouslySetInnerHTML={{ __html: cdekAnalyticsNoscript }} />
          </>
        )}
        <script
          defer
          type="text/javascript"
          src="https://messenger.upservice.io/api/widget/8082260e-28e4-4719-9f71-cf96ce31741a"
        />
      </Head>
      {/* <StyleProvider> */}
      <PartnerProvider>
        <OldThemeProvider>
          <ThemeProvider>
            <ToastProvider>
              {/* <ToastProvider> */}
              <BreadCrumbsProvider>{getLayout(<Component {...pageProps} />, pageProps)}</BreadCrumbsProvider>
            </ToastProvider>
            {/* </ToastProvider> */}
          </ThemeProvider>
        </OldThemeProvider>
      </PartnerProvider>
      {/* </StyleProvider> */}

      <Script
        id="mts-analitic"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: mtsAnaliticScript,
        }}
      />

      <Script
        defer
        id="gtm-src"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: gtmScript,
        }}
      />

      <noscript dangerouslySetInnerHTML={{ __html: gtmNoScript }} />
    </>
  )
}

export default App
